import { createShallowStore } from "replo-utils/react/zustand";

type PageTabStatus = "new" | "edited" | null;

type PageTab = {
  id: string;
  content: PageTabContent;
};

type PageTabContentType = "page" | "ad" | "email" | "report";

type PageTabContent = {
  type: PageTabContentType;
  pageRoute: string;
  status: PageTabStatus;
};

const MAX_RECENTLY_CLOSED = 10;

type PageTabsActions = {
  openTab: (tab: PageTab) => void;
  closeTab: (id: string) => void;
  clearTabStatus: (id: string) => void;
  reopenTab: (id: string) => void;
};

type PageTabsStore = {
  openTabs: PageTab[];
  recentlyClosedTabs: PageTab[];
  actions: PageTabsActions;
};

const usePageTabsStore = createShallowStore<PageTabsStore>((set, get) => ({
  openTabs: [],
  recentlyClosedTabs: [],

  actions: {
    openTab: (tab) => {
      const { openTabs } = get();
      const existing = openTabs.find((t) => t.id === tab.id);
      if (existing) {
        const shouldUpdateStatus =
          tab.content.type === "page" &&
          tab.content.status !== null &&
          existing.content.status !== "new";
        if (shouldUpdateStatus) {
          set({
            openTabs: openTabs.map((t) => {
              return t.id === tab.id
                ? {
                    ...t,
                    content: {
                      ...tab.content,
                      status: tab.content.status,
                    },
                  }
                : t;
            }),
          });
        }
        return;
      }
      set({
        openTabs: [...openTabs, tab],
        recentlyClosedTabs: get().recentlyClosedTabs.filter(
          (t) => t.id !== tab.id,
        ),
      });
    },

    closeTab: (id) => {
      const { openTabs, recentlyClosedTabs } = get();
      const closedTab = openTabs.find((t) => t.id === id);
      set({
        openTabs: openTabs.filter((t) => t.id !== id),
        recentlyClosedTabs: closedTab
          ? [closedTab, ...recentlyClosedTabs].slice(0, MAX_RECENTLY_CLOSED)
          : recentlyClosedTabs,
      });
    },

    clearTabStatus: (id) => {
      set((state) => ({
        openTabs: state.openTabs.map((t) => {
          return t.id === id
            ? { ...t, content: { ...t.content, status: null } }
            : t;
        }),
      }));
    },

    reopenTab: (id) => {
      const { openTabs, recentlyClosedTabs } = get();
      const tab = recentlyClosedTabs.find((t) => t.id === id);
      if (!tab) {
        return;
      }
      const alreadyOpen = openTabs.some((t) => t.id === id);
      set({
        openTabs: alreadyOpen ? openTabs : [...openTabs, tab],
        recentlyClosedTabs: recentlyClosedTabs.filter((t) => t.id !== id),
      });
    },
  },
}));

export const useOpenTabs = () => usePageTabsStore((s) => s.openTabs);
export const useRecentlyClosedTabs = () => {
  return usePageTabsStore((s) => s.recentlyClosedTabs);
};
export const usePageTabsActions = () => usePageTabsStore((s) => s.actions);

/**
 * Read the current open tabs snapshot outside of React (e.g. after a
 * synchronous zustand update in an event handler).
 */
export function getOpenTabsSnapshot(): PageTab[] {
  return usePageTabsStore.getState().openTabs;
}

export function getActiveTabId({
  openTabs,
  pageRoute,
}: {
  openTabs: PageTab[];
  pageRoute: string | null;
}): string | null {
  if (pageRoute) {
    return openTabs.find((t) => t.id === pageRoute)?.id ?? null;
  }
  return null;
}

export type { PageTab, PageTabContentType };
